import { useState, useCallback, useEffect, useRef } from 'react';
import { getApiUrl } from '../config';
import type { Friend, FriendRequest, SentRequest } from '../types';

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

export function useFriends(options?: {
  onError?: (message: string) => void;
  onAccept?: () => void;
  onReject?: () => void;
  onCancel?: () => void;
  onRemove?: () => void;
}) {
  const [friends, setFriends] = useState<Friend[]>([]);
  const [requests, setRequests] = useState<FriendRequest[]>([]);
  const [sentRequests, setSentRequests] = useState<SentRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const onErrorRef = useRef(options?.onError);
  const onAcceptRef = useRef(options?.onAccept);
  const onRejectRef = useRef(options?.onReject);
  const onCancelRef = useRef(options?.onCancel);
  const onRemoveRef = useRef(options?.onRemove);
  useEffect(() => {
    onErrorRef.current = options?.onError;
    onAcceptRef.current = options?.onAccept;
    onRejectRef.current = options?.onReject;
    onCancelRef.current = options?.onCancel;
    onRemoveRef.current = options?.onRemove;
  }, [
    options?.onError,
    options?.onAccept,
    options?.onReject,
    options?.onCancel,
    options?.onRemove,
  ]);

  const fetchFriends = useCallback(async () => {
    try {
      const res = await fetch(getApiUrl('/api/friends'), { headers: getAuthHeaders() });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        onErrorRef.current?.(data.error || 'Error al cargar amigos');
        return;
      }
      const data = await res.json();
      setFriends(data.friends || []);
    } catch {
      onErrorRef.current?.('Error de conexión');
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    try {
      const res = await fetch(getApiUrl('/api/friends/requests'), { headers: getAuthHeaders() });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        onErrorRef.current?.(data.error || 'Error al cargar solicitudes');
        return;
      }
      const data = await res.json();
      setRequests(data.requests || []);
    } catch {
      onErrorRef.current?.('Error de conexión');
    }
  }, []);

  const fetchSentRequests = useCallback(async () => {
    try {
      const res = await fetch(getApiUrl('/api/friends/requests/sent'), {
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        onErrorRef.current?.(data.error || 'Error al cargar enviadas');
        return;
      }
      const data = await res.json();
      setSentRequests(data.requests || []);
    } catch {
      onErrorRef.current?.('Error de conexión');
    }
  }, []);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([fetchFriends(), fetchRequests(), fetchSentRequests()]);
    } finally {
      setLoading(false);
    }
  }, [fetchFriends, fetchRequests, fetchSentRequests]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const acceptRequest = useCallback(
    async (requestId: string) => {
      setAcceptingId(requestId);
      try {
        const res = await fetch(getApiUrl(`/api/friends/accept/${requestId}`), {
          method: 'POST',
          headers: getAuthHeaders(),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          onErrorRef.current?.(data.error || 'Error al aceptar');
          return;
        }
        setRequests((prev) => prev.filter((r) => r.id !== requestId));
        await fetchFriends();
        onAcceptRef.current?.();
      } catch {
        onErrorRef.current?.('Error de conexión');
      } finally {
        setAcceptingId(null);
      }
    },
    [fetchFriends]
  );

  const rejectRequest = useCallback(async (requestId: string) => {
    setRejectingId(requestId);
    try {
      const res = await fetch(getApiUrl(`/api/friends/reject/${requestId}`), {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        onErrorRef.current?.(data.error || 'Error al rechazar');
        return;
      }
      setRequests((prev) => prev.filter((r) => r.id !== requestId));
      onRejectRef.current?.();
    } catch {
      onErrorRef.current?.('Error de conexión');
    } finally {
      setRejectingId(null);
    }
  }, []);

  const cancelRequest = useCallback(async (requestId: string) => {
    setCancellingId(requestId);
    try {
      const res = await fetch(getApiUrl(`/api/friends/requests/${requestId}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        onErrorRef.current?.(data.error || 'Error al cancelar');
        return;
      }
      setSentRequests((prev) => prev.filter((r) => r.requestId !== requestId));
      onCancelRef.current?.();
    } catch {
      onErrorRef.current?.('Error de conexión');
    } finally {
      setCancellingId(null);
    }
  }, []);

  const removeFriend = useCallback(async (friendId: string) => {
    setRemovingId(friendId);
    try {
      const res = await fetch(getApiUrl(`/api/friends/${friendId}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        onErrorRef.current?.(data.error || 'Error al eliminar amigo');
        return false;
      }
      setFriends((prev) => prev.filter((f) => f.id !== friendId));
      onRemoveRef.current?.();
      return true;
    } catch {
      onErrorRef.current?.('Error de conexión');
      return false;
    } finally {
      setRemovingId(null);
    }
  }, []);

  return {
    friends,
    requests,
    sentRequests,
    loading,
    refetch,
    acceptRequest,
    rejectRequest,
    cancelRequest,
    removeFriend,
    acceptingId,
    rejectingId,
    cancellingId,
    removingId,
  };
}
